import { supabase } from "@/integrations/supabase/client";

// URL template interfaces
export interface UrlTemplate {
  id: string;
  template_type: 'user_invitation' | 'partner_invitation' | 'proposal_acceptance' | 'invitation_signup';
  name: string;
  url_template: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  content_config?: {
    base_url?: string;
    page_title?: string;
    heading?: string;
    subheading?: string;
    description?: string;
    button_text?: string;
    footer?: string;
    logo_url?: string;
    benefits_heading?: string;
    benefits?: string[];
  };
  color_config?: {
    primary_color?: string;
    secondary_color?: string;
    background_color?: string;
    text_color?: string;
    button_color?: string;
    button_text_color?: string;
    button_hover_color?: string;
    contact_panel_color?: string;
    organization_panel_color?: string;
  };
  font_config?: {
    font_family?: string;
    heading_font_family?: string;
    font_size?: string;
    heading_font_size?: string;
  };
}

export interface UrlTemplateDB {
  id: string;
  template_type: string;
  name: string;
  url_template: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
  content_config?: any;
  color_config?: any;
  font_config?: any;
}

/**
 * Service for managing URL templates
 */
export class UrlTemplateService {
  
  /**
   * Get all URL templates
   */
  static async getAllTemplates(): Promise<UrlTemplate[]> {
    const { data, error } = await supabase
      .from('url_templates')
      .select('*')
      .order('template_type', { ascending: true })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching URL templates:', error);
      throw new Error(`Failed to fetch URL templates: ${error.message}`);
    }

    return data.map(this.mapDbToTemplate);
  }

  /**
   * Get active URL template for a specific type
   */
  static async getActiveTemplate(templateType: UrlTemplate['template_type']): Promise<UrlTemplate | null> {
    const { data, error } = await supabase
      .from('url_templates')
      .select('*')
      .eq('template_type', templateType)
      .eq('is_active', true)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No active template found
        return null;
      }
      console.error('Error fetching active URL template:', error);
      throw new Error(`Failed to fetch active URL template: ${error.message}`);
    }

    return this.mapDbToTemplate(data);
  }

  /**
   * Save a new URL template
   */
  static async saveTemplate(template: Omit<UrlTemplate, 'id' | 'created_at' | 'updated_at'>): Promise<UrlTemplate> {
    const { data, error } = await supabase
      .from('url_templates')
      .insert([{
        template_type: template.template_type,
        name: template.name,
        url_template: template.url_template,
        description: template.description,
        is_active: template.is_active,
        created_by: template.created_by,
        content_config: template.content_config,
        color_config: template.color_config,
        font_config: template.font_config
      }])
      .select()
      .single();

    if (error) {
      console.error('Error saving URL template:', error);
      throw new Error(`Failed to save URL template: ${error.message}`);
    }

    return this.mapDbToTemplate(data);
  }

  /**
   * Update an existing URL template
   */
  static async updateTemplate(id: string, updates: Partial<UrlTemplate>): Promise<UrlTemplate> {
    const { data, error } = await supabase
      .from('url_templates')
      .update({
        name: updates.name,
        url_template: updates.url_template,
        description: updates.description,
        is_active: updates.is_active,
        template_type: updates.template_type,
        content_config: updates.content_config,
        color_config: updates.color_config,
        font_config: updates.font_config
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating URL template:', error);
      throw new Error(`Failed to update URL template: ${error.message}`);
    }

    return this.mapDbToTemplate(data);
  }

  /**
   * Delete a URL template
   */
  static async deleteTemplate(id: string): Promise<void> {
    const { error } = await supabase
      .from('url_templates')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting URL template:', error);
      throw new Error(`Failed to delete URL template: ${error.message}`);
    }
  }

  /**
   * Set a template as active for its type (deactivates others of same type)
   */
  static async setAsActive(id: string, templateType: string): Promise<void> {
    // First, deactivate all templates of this type
    await supabase
      .from('url_templates')
      .update({ is_active: false })
      .eq('template_type', templateType);

    // Then activate the selected template
    const { error } = await supabase
      .from('url_templates')
      .update({ is_active: true })
      .eq('id', id);

    if (error) {
      console.error('Error setting URL template as active:', error);
      throw new Error(`Failed to set URL template as active: ${error.message}`);
    }
  }

  /**
   * Process URL template with variables
   */
  static processUrlTemplate(template: string, variables: Record<string, any>): string {
    return Object.entries(variables).reduce((result, [key, value]) => {
      const placeholder = `{{${key}}}`;
      return result.replace(new RegExp(placeholder, 'g'), String(value || ''));
    }, template);
  }

  /**
   * Get available variables for a template type
   */
  static getAvailableVariables(templateType: UrlTemplate['template_type']) {
    const baseVariables = [
      { key: 'baseUrl', description: 'Base URL of the application (e.g., https://app.snapshot.live)' }
    ];

    switch (templateType) {
      case 'user_invitation':
        return [
          ...baseVariables,
          { key: 'invitationToken', description: 'Unique invitation token/ID' },
          { key: 'contactId', description: 'Contact ID being invited' },
          { key: 'organizationId', description: 'Organization ID' },
          { key: 'invitedRole', description: 'Role being invited to (e.g., admin, standard)' }
        ];
      
      case 'partner_invitation':
        return [
          ...baseVariables,
          { key: 'invitationToken', description: 'Unique invitation token/ID' },
          { key: 'partnerId', description: 'Partner ID being invited' },
          { key: 'partnerName', description: 'Full name of the partner (e.g., "John Smith")' },
          { key: 'partnerNameSlug', description: 'URL-friendly partner name (e.g., "john-smith")' }
        ];
      
      case 'proposal_acceptance':
        return [
          ...baseVariables,
          { key: 'proposalToken', description: 'Unique proposal access token' },
          { key: 'contactId', description: 'Contact ID receiving the proposal' }
        ];
      
      case 'invitation_signup':
        return [
          ...baseVariables,
          { key: 'invitationToken', description: 'Unique invitation token/ID' },
          { key: 'contactId', description: 'Contact ID being invited' },
          { key: 'organizationId', description: 'Organization ID' },
          { key: 'organizationName', description: 'Organization name' },
          { key: 'contactName', description: 'Contact full name' },
          { key: 'invitedRole', description: 'Role being invited to' }
        ];
      
      default:
        return baseVariables;
    }
  }

  /**
   * Generate mock variables for preview
   */
  static getMockVariables(templateType: UrlTemplate['template_type']) {
    const baseMock = {
      baseUrl: 'https://app.snapshot.live'
    };

    switch (templateType) {
      case 'user_invitation':
        return {
          ...baseMock,
          invitationToken: 'inv_abc123def456',
          contactId: 'contact_789',
          organizationId: 'org_456',
          invitedRole: 'standard'
        };
      
      case 'partner_invitation':
        return {
          ...baseMock,
          invitationToken: 'part_inv_xyz789',
          partnerId: 'partner_123',
          partnerName: 'John Smith',
          partnerNameSlug: 'john-smith'
        };
      
      case 'proposal_acceptance':
        return {
          ...baseMock,
          proposalToken: 'prop_uvw456',
          contactId: 'contact_321'
        };
      
      case 'invitation_signup':
        return {
          ...baseMock,
          invitationToken: 'inv_signup_abc123',
          contactId: 'contact_789',
          organizationId: 'org_456',
          organizationName: 'Acme Corporation',
          contactName: 'John Smith',
          invitedRole: 'admin'
        };
      
      default:
        return baseMock;
    }
  }

  /**
   * Map database record to UrlTemplate interface
   */
  private static mapDbToTemplate(data: UrlTemplateDB): UrlTemplate {
    return {
      id: data.id,
      template_type: data.template_type as UrlTemplate['template_type'],
      name: data.name,
      url_template: data.url_template,
      description: data.description,
      is_active: data.is_active,
      created_at: data.created_at,
      updated_at: data.updated_at,
      created_by: data.created_by,
      content_config: data.content_config,
      color_config: data.color_config,
      font_config: data.font_config
    };
  }
}

export const urlTemplateService = UrlTemplateService;